import { useInfiniteQuery } from 'react-query';
import { ChildTemplateResponse } from '../_types';
import { performanceReportingApiUrl, fetcher, handleError, ServerErrorResponse, createParamString } from 'data_sources/constants';

// Fetch child templates from API
export const getChildTemplates = async (
  templateId: number,
  params: Record<string, any> = {},
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<ChildTemplateResponse> => {
  try {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${templateId}/children`;

    // Build query parameters
    const queryParams = createParamString(params);
    const fullUrl = queryParams ? `${url}?${queryParams}` : url;

    const response = await fetcher({
      url: fullUrl,
      method: 'GET',
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export const useChildTemplates = (
  templateId: number,
  params: Record<string, any> = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) => {
  return useInfiniteQuery<ChildTemplateResponse, ServerErrorResponse>(
    ['childTemplates', templateId, JSON.stringify(params)],
    async ({ pageParam, signal }) => {
      const combinedParams = {
        ...params,
        nextToken: pageParam,
      };
      return await getChildTemplates(templateId, combinedParams, signal, onError);
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
      enabled: !!templateId,
      ...options,
    }
  );
};
